import { useState, useEffect, useMemo } from 'react'
import { Collapse, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronLeftRight from '@mui/icons-material/ChevronRight'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ExpandLess from '@mui/icons-material/ExpandLess'
import { MuiDrawer } from 'theme/themeOverride'
import styles from './MenuBar.module.scss'
import { TypoGraph, Tooltip, Icon } from 'components/ui'
import { Link } from 'react-router-dom'
import { footerMenuBar } from './menuBarData'
import usePermissions from 'hooks/usePermissions'
import strings from 'l10n'
import useFeatureFlag from 'hooks/useFeatureFlag'
import { NavigationImageComponent } from './NavigationImageComponent'

const reportingEnabled = process.env.REACT_APP_FEATURE_REPORTS?.toLowerCase() === 'true'

type MenuBarProps = {
  path: string
  navigate: Function
}

export type MenuBarListItem = {
  name: string
  icon?: string
  id: string
  subItems?: MenuBarListItem[]
}

type MenuListItemProps = {
  item: MenuBarListItem
  selectedItem: string
  isMenubarOpen: boolean
}

type MenuListProps = {
  data: MenuBarListItem[]
  isMenubarOpen: boolean
  selectedItem: string
  className?: string
}

function MenuListItem(props: MenuListItemProps) {
  const { item, selectedItem, isMenubarOpen } = props

  const selected = selectedItem === item.id

  const iconClass = selected ? 'active' : 'disabled'
  let itemActiveClass = ''
  if (selected) itemActiveClass = 'active'

  return (
    <Tooltip key={item.name} title={item.name} placement="right" show={!isMenubarOpen}>
      <ListItem disablePadding sx={{ display: 'block' }} className={`fc-menu-bar-item ${itemActiveClass}`}>
        <Link to={item.id} className={styles.openInTab}>
          <ListItemButton
            sx={{
              height: isMenubarOpen ? 50 : 55,
              justifyContent: 'initial',
              borderRadius: 2,
              marginTop: 0,
              marginBottom: 0,
              flexDirection: isMenubarOpen ? 'row' : 'column',
            }}
            selected={selected}
          >
            {item.icon && (
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isMenubarOpen ? 1 : 0,
                  justifyContent: 'center',
                }}
              >
                <Icon name={`${item.icon}_${iconClass}`} size={25} boxClassName={iconClass} />
              </ListItemIcon>
            )}
            <TypoGraph variant="body1" mb={0} sx={!isMenubarOpen ? { fontSize: '11px', marginTop: '2px' } : {}} link>
              {item.name}
            </TypoGraph>
          </ListItemButton>
        </Link>
      </ListItem>
    </Tooltip>
  )
}

function MenuListItemCollapse(props: MenuListItemProps) {
  const { item, selectedItem, isMenubarOpen } = props
  const [open, setOpen] = useState(true)

  return (
    <>
      <Tooltip key={item.name} title={item.name} placement="right" show={!isMenubarOpen}>
        <ListItem disablePadding sx={{ display: 'block' }} className="fc-menu-bar-item">
          <ListItemButton
            sx={{
              height: isMenubarOpen ? 50 : 55,
              justifyContent: 'initial',
              borderRadius: 2,
              marginTop: 1,
              marginBottom: 1,
              flexDirection: isMenubarOpen ? 'row' : 'column',
            }}
            onClick={() => setOpen((prev) => !prev)}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: isMenubarOpen ? 1 : 0,
                justifyContent: 'center',
              }}
            >
              <Icon name={item.icon} size={25} boxClassName={selectedItem === item.id ? 'active' : ''} />
            </ListItemIcon>
            <ListItemText
              primary={item.name}
              primaryTypographyProps={{
                sx: !isMenubarOpen ? { fontSize: '11px', marginTop: '2px' } : {},
              }}
            />
            {open ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
      </Tooltip>
      {isMenubarOpen && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List sx={{ pl: 2, pt: 0, pb: 0 }}>
            {item.subItems?.map((subItem) => (
              <MenuListItem key={subItem.id} item={subItem} selectedItem={selectedItem} isMenubarOpen={isMenubarOpen} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  )
}

const MenuList = (props: MenuListProps) => {
  const { data, isMenubarOpen, selectedItem, className } = props
  return (
    <List className={className ? className : styles.menuList}>
      {data.map((item) =>
        item.subItems ? (
          <MenuListItemCollapse key={item.id} item={item} selectedItem={selectedItem} isMenubarOpen={isMenubarOpen} />
        ) : (
          <MenuListItem key={item.id} item={item} selectedItem={selectedItem} isMenubarOpen={isMenubarOpen} />
        )
      )}
    </List>
  )
}

function MenuBar(props: MenuBarProps) {
  const { path } = props
  const [selectedItem, setSelectedItem] = useState('companies')
  const [showCloseIcon, setShowCloseIcon] = useState(false)
  const [open, setOpen] = useState(true)
  const { enableCreditCheck } = useFeatureFlag()
  const {
    canReadInvoices,
    canAccessDebtors,
    canUploadDocumentToCreateDraftInvoice,
    canAccessReports,
    canReadDashboard,
  } = usePermissions()

  const shouldDisplayReports = reportingEnabled && canAccessReports

  const mainMenuBar: MenuBarListItem[] = useMemo(() => {
    const menu: MenuBarListItem[] = []

    if (canReadDashboard) {
      menu.push({
        name: strings.DASHBOARD,
        icon: 'menubar/icon_dashboard',
        id: 'dashboard',
      })
    }

    if (canReadInvoices) {
      menu.push({
        name: strings.INVOICES,
        icon: 'menubar/icon_invoices',
        id: 'invoices',
      })
    }

    if (enableCreditCheck && canAccessDebtors) {
      menu.push({
        name: strings.DEBTORS,
        icon: 'menubar/icon_debtors',
        id: 'debtors',
      })
    }

    if (canUploadDocumentToCreateDraftInvoice) {
      menu.push({
        name: strings.UPLOAD,
        icon: 'menubar/icon_upload',
        id: 'upload',
      })
    }

    if (shouldDisplayReports) {
      menu.push({
        name: strings.REPORTS,
        icon: 'menubar/icon_reports',
        id: 'reports',
      })
    }

    return menu
  }, [
    enableCreditCheck,
    canReadInvoices,
    canAccessDebtors,
    canUploadDocumentToCreateDraftInvoice,
    shouldDisplayReports,
  ])

  /*
   * This useffect will trigger whenever the current url path changes
   * It will check the current path and menubar list id matches once it matched,
   * It will set the active menuBarItem to hightlight.
   */
  useEffect(() => {
    const flatMainMenu = mainMenuBar.flatMap((item) => {
      if (!item.subItems) {
        return item
      } else {
        return [item, ...item.subItems]
      }
    })
    const flatFooterMenu = footerMenuBar.flatMap((item) => {
      if (!item.subItems) {
        return item
      } else {
        return [item, ...item.subItems]
      }
    })
    const menuItems = [...flatMainMenu, ...flatFooterMenu]

    const findItem = menuItems.find((ele: MenuBarListItem) => {
      return path.includes(`/${ele.id}`) ? ele : null
    })
    if (findItem) {
      setSelectedItem(findItem.id)
    }
  }, [path, mainMenuBar])

  const handleDrawerOpen = () => {
    setOpen(!open)
  }

  const openCloseIconEvent = {
    onMouseOver: () => setShowCloseIcon(true),
    onMouseLeave: () => {
      setTimeout(() => {
        if (!showCloseIcon) {
          setShowCloseIcon(false)
        }
      }, 800)
    },
    onMouseOut: () => setShowCloseIcon(false),
  }

  return (
    <div {...openCloseIconEvent} className="fc-menu-bar">
      {showCloseIcon && (
        <Icon boxClassName={`${styles.leftIconButton} ${!open && styles.rightIconButton}`} onClick={handleDrawerOpen}>
          {open ? <ChevronLeftIcon sx={{ color: 'black' }} /> : <ChevronLeftRight sx={{ color: 'black' }} />}
        </Icon>
      )}
      <MuiDrawer variant="permanent" open={open}>
        <NavigationImageComponent open={open} />
        <MenuList isMenubarOpen={open} data={mainMenuBar} selectedItem={selectedItem} />
        <MenuList
          isMenubarOpen={open}
          data={footerMenuBar}
          selectedItem={selectedItem}
          className={styles.stickyMenuList}
        />
      </MuiDrawer>
    </div>
  )
}

export default MenuBar
